import { ReleasableCommits, typescript } from "projen";
import {
  NodePackageManager,
  Transform,
  UpgradeDependenciesSchedule,
} from "projen/lib/javascript";
import { ReleaseTrigger } from "projen/lib/release";
import { merge } from "ts-deepmerge";
import { CHANGELOG_TYPES, resolveVersionrcOptions } from "./changelog-types";
import { MonorepoProject } from "./monorepo-project";
import { applyPackagePrivate, PackagePrivateOptions } from "./package-private";
import { resolveTypeScriptProjectOutdir } from "./resolve-outdir";
import { AgentConfig } from "../agent/agent-config";
import { AgentConfigOptions } from "../agent/agent-config-options";
import { ApiExtractor, ApiExtractorOptions } from "../api-extractor";
import { PnpmWorkspace } from "../pnpm";
import { ResetTask, ResetTaskOptions } from "../tasks/reset-task";
import { TurboRepo } from "../turbo";
import { TsdocConfig, TsdocConfigOptions } from "../typescript";
import { Vitest, VitestOptions } from "../vitest";

/**
 * Test runner for TypeScript projects.
 */
export const TestRunner = {
  JEST: "jest",
  VITEST: "vitest",
} as const;

export type TestRunner = (typeof TestRunner)[keyof typeof TestRunner];

/**
 * Configuration options for TypeScriptProject.
 */
export interface TypeScriptProjectOptions
  extends
    Omit<typescript.TypeScriptProjectOptions, "defaultReleaseBranch">,
    PackagePrivateOptions {
  /**
   * Test runner to use.
   *
   * @default TestRunner.JEST
   */
  readonly testRunner?: TestRunner;

  /**
   * Options for Vitest (only used when testRunner is 'vitest').
   */
  readonly vitestOptions?: VitestOptions;

  /**
   * Enable the reset task that deletes all build artifacts.
   *
   * @default true
   */
  readonly resetTask?: boolean;

  /**
   * Options for the reset task.
   */
  readonly resetTaskOptions?: ResetTaskOptions;

  /**
   * AI agent configuration (Cursor, Claude Code rules).
   * Generates rule files with auto-detected bundles based on project tooling.
   *
   * - `true` or `{}`: enable with auto-detected defaults
   * - `AgentConfigOptions`: enable with explicit configuration
   * - `false` or `undefined`: disabled (default)
   *
   * Sub-projects do not inherit `AgentConfig` from their parent
   * `MonorepoProject`. Agent rule files are rendered only on projects that
   * explicitly opt in, so a monorepo's root `AgentConfig` does not duplicate
   * `.cursor/`, `.claude/`, or `CLAUDE.md` into each sub-project's `outdir`.
   *
   * @default false
   */
  readonly agentConfig?: AgentConfigOptions | boolean;

  /**
   * Configure the `@microsoft/api-extractor` runner used by the
   * docs-sync pipeline for public-API drift detection. When omitted
   * the extractor is enabled with default options (rollups write to
   * `.api-extractor/` and are gitignored). Set to `false` to opt a
   * package out of drift detection entirely.
   *
   * @default `{}`
   */
  readonly apiExtractor?: ApiExtractorOptions | false;

  /**
   * Configure the emitted `tsdoc.json` so `eslint-plugin-tsdoc` recognises
   * workspace-scope package references (`@codedrifters/...`, `@openhi/...`,
   * etc.) and common JSDoc convention tags such as default, defaultValue,
   * experimental, and the internal-marker tag.
   *
   * Pass `false` to opt out of emitting `tsdoc.json` entirely. The
   * `tsdoc-at-sign-in-word` and `tsdoc-undefined-tag` lint warnings will
   * return at full volume in that case.
   *
   * Note: the tag names above are intentionally written without their
   * leading at-sign. `tsc --stripInternal` (which configulator enables)
   * strips any declaration whose JSDoc contains the bare at-sign-prefixed
   * `internal` tag — even when the token sits inside a backtick code span
   * — and that strips this field from the published `.d.ts` rollup.
   *
   * @default `{}`
   */
  readonly tsdocConfig?: TsdocConfigOptions | false;

  /**
   * Control the `jsdoc/require-jsdoc` lint rule applied to `src/**` files.
   *
   * `eslint-plugin-jsdoc`'s `require-jsdoc` rule emits one warning per
   * un-documented function / method / variable / interface / type / enum
   * declaration. On a typical consuming repo that's ~1,000 warnings — the
   * cost-benefit doesn't justify forcing prose comments on every internal
   * helper.
   *
   * - `"off"` (default) — the rule is disabled. Consumers rely on
   *   `tsdoc/syntax`, `jsdoc/check-tag-names`, and `jsdoc/check-types` for
   *   the comment-quality value `eslint-plugin-jsdoc` actually adds.
   * - `"public"` — the rule is enabled at `warn` level and scoped to
   *   exported declarations only (`publicOnly: true`). Restores the
   *   previous behaviour for projects that want it.
   * - `"all"` — the rule is enabled at `warn` level on every function /
   *   method declaration in `src/**`, public or private. Reserved for
   *   strict documentation regimes.
   *
   * Tests, spec files, and `*.generated.ts` are always excluded.
   *
   * @default "off"
   */
  readonly requireJsdoc?: "off" | "public" | "all";
}

export class TypeScriptProject extends typescript.TypeScriptProject {
  constructor(userOptions: TypeScriptProjectOptions) {
    /**
     * Configulator requires every project to live inside a MonorepoProject.
     * The catalog-based `@types/node` pin and the workspace install hook both
     * assume the parent exists.
     */
    if (!(userOptions.parent instanceof MonorepoProject)) {
      throw new Error(
        "TypeScriptProject must be parented to a MonorepoProject. Pass `parent: <MonorepoProject>` in the project options.",
      );
    }

    const parent = userOptions.parent;
    const pnpmVersion = parent.pnpmVersion;
    const pnpmWorkspace = PnpmWorkspace.of(parent);

    /*************************************************************************
     *
     * Resolve outdir
     *
     * When the caller omits `outdir`, derive it from the scoped package
     * name (`packageName ?? name`) as `packages/<scope>/<name>`. This
     * enforces the monorepo-layout contract (ADR-006) without requiring
     * every sub-project to restate its own path.
     *
     ************************************************************************/

    const resolvedOutdir =
      userOptions.outdir ??
      resolveTypeScriptProjectOutdir(
        userOptions.packageName ?? userOptions.name,
      );

    /*************************************************************************
     *
     * Default Options
     *
     ************************************************************************/

    const testRunner = userOptions.testRunner ?? TestRunner.JEST;
    const useJest = testRunner === TestRunner.JEST;

    const defaultOptions: Omit<TypeScriptProjectOptions, "name"> & {
      defaultReleaseBranch: string;
    } = {
      /**
       * This is a standard, so don't require it to passed in everywhere.
       */
      defaultReleaseBranch: "main",

      /**
       * Outdir defaults to `packages/<scope>/<name>` when the caller
       * omits it. Explicit overrides in `userOptions` always win via
       * the merge below.
       */
      outdir: resolvedOutdir,

      /**
       * Enable reset task by default.
       */
      resetTask: true,

      /**
       * Packaging options
       */
      packageManager: NodePackageManager.PNPM,
      pnpmVersion: pnpmVersion,
      licensed: userOptions.license !== undefined || false,
      copyrightOwner: "CodeDrifters",
      release: false,

      /**
       * Don't add sample code.
       */
      sampleCode: false,

      ...(useJest
        ? {
            /**
             * Make sure jest config is stored outside of package.json
             */
            jestOptions: {
              configFilePath: "jest.config.json",
              jestConfig: {
                roots: [`<rootDir>/src`],
                transform: {
                  ["^.+\\.[t]sx?$"]: new Transform("@swc/jest"),
                },
                moduleFileExtensions: ["js", "ts"],
              },
            },
            /**
             * SWC for faster testing
             */
            devDeps: ["@swc/jest", "@swc/core"] as string[],
          }
        : {
            jest: false,
            devDeps: [] as string[],
          }),

      /**
       * Turn on prettier formatting
       */
      prettier: true,

      /**
       * Don't package test files.
       */
      npmIgnoreOptions: {
        ignorePatterns: ["*.spec.*", "*.test.*"],
      },

      /**
       * Tests are co-located in `src`, so projen 0.100.4+'s test-directory
       * tsconfig would only ever create an empty `test/` folder holding a
       * lone `test/tsconfig.json`. Disable it — the main `tsconfig.json`
       * already covers the co-located tests for the typescript-eslint
       * project service (see #830), so a separate dev/test config is dead
       * weight.
       */
      disableTsconfigDev: true,

      /**
       * Options for the automated dependency upgrade task / workflow
       * Automatically exclude any packages that are managed by the root
       * project as default catalog dependencies since we want to let the
       * catalog manage those dependencies.
       */
      depsUpgrade: true,
      depsUpgradeOptions: {
        workflow: false,
        exclude: Object.keys(pnpmWorkspace?.defaultCatalog || {}),
        ...(userOptions.parent && userOptions.parent instanceof MonorepoProject
          ? {
              workflowOptions: {
                schedule: UpgradeDependenciesSchedule.WEEKLY,
              },
              cooldown: 7,
            }
          : {}),
      },

      /**
       * Only release when the package's own sources or `package.json`
       * (version, dependencies) change.
       *
       * Deliberately narrower than the `"."` pathspec the bump decision and
       * the changelog use below. Widening this to `<outdir>/**` would make
       * all three filters select exactly the same files, but it also makes
       * every package-local edit releasable — a test-only change, or the
       * `.projen/` regeneration that a sibling package's change produces. In
       * this monorepo that regeneration is routine, so the aligned trigger
       * publishes versions whose tarballs are byte-identical to the previous
       * one.
       *
       * The asymmetry that remains is benign: a package-local commit outside
       * `src` cannot start a release on its own, but is still described in
       * the notes of the next release that something else triggers. Notes
       * covering slightly more than the trigger is the right direction for
       * the two to disagree in.
       */
      releaseTrigger: ReleaseTrigger.continuous({
        paths: [`${resolvedOutdir}/src/**`, `${resolvedOutdir}/package.json`],
      }),

      /**
       * Scope the other two release filters to the package folder, the same
       * slice of history the trigger above reads from.
       *
       * `versionrcOptions.path` reaches `commit-and-tag-version`, which
       * forwards it to `conventional-changelog` as a `git log` pathspec —
       * without it the changelog between two package-scoped tags spans every
       * commit in the monorepo. `releasableCommits` appends the same pathspec
       * to the command that decides whether to bump at all; without it a
       * package with no relevant changes still cuts a release, just with an
       * empty changelog.
       *
       * `"."` rather than `resolvedOutdir`: both commands run with their
       * working directory already set to the package outdir. A single string
       * is all `path` accepts — `git-raw-commits` splices it into `git log`
       * as one argv entry (`push("--", path)`), so the pathspec cannot be a
       * list, so it cannot be narrowed to `src` plus `package.json` to match
       * the trigger exactly.
       *
       * `types` gives every conventional-commit type a visible section. The
       * preset hides or discards everything but `feat` and `fix` by default,
       * while still bumping for them, which is what makes a package-local
       * churn release ship notes holding nothing but a version heading. See
       * `CHANGELOG_TYPES`.
       */
      versionrcOptions: { path: ".", types: [...CHANGELOG_TYPES] },
      releasableCommits: ReleasableCommits.everyCommit("."),
    };

    /*************************************************************************
     *
     * Merge defaults into user options
     *
     ************************************************************************/

    const mergedOptions = merge(defaultOptions, userOptions);

    const options: TypeScriptProjectOptions &
      typescript.TypeScriptProjectOptions = {
      ...mergedOptions,

      /**
       * `ts-deepmerge` concatenates arrays, so a supplied changelog `types`
       * arrives appended to `CHANGELOG_TYPES` — and the preset resolves each
       * commit with `types.find(...)`, so our entry would win every lookup
       * and the consumer's list would be inert. Replace, don't concatenate.
       */
      versionrcOptions: resolveVersionrcOptions(
        mergedOptions.versionrcOptions,
        userOptions.versionrcOptions,
      ),
    };

    super(options);

    /**
     * Use catalog version for `@types/node` so all packages share a single
     * pinned version (overrides projen's default `@types/node@*`).
     */
    this.addDevDeps("@types/node@catalog:");

    /**
     * The main tsconfig now includes co-located `src/**` test files (no
     * `**\/*.test.*` exclude) so projen 0.100.4+'s typescript-eslint project
     * service can resolve them (issue #830). Because `tsc --build` also uses
     * the main tsconfig, skip type-checking of dependency declaration files
     * so the compile doesn't fail on test-runner types — Vitest pulls in
     * Vite's `.d.ts`, which don't type-check under the package's classic
     * `node` moduleResolution.
     */
    this.tsconfig?.file.addOverride("compilerOptions.skipLibCheck", true);

    /**
     * When using Jest, remove ts-jest since we use `@swc/jest`.
     * When using Vitest, add the Vitest component (test task + config + deps).
     */
    if (options.testRunner === TestRunner.VITEST) {
      new Vitest(this, {
        ...options.vitestOptions,
        config: {
          include: ["src/**/*.{test,spec}.?(c|m)[jt]s?(x)"],
          ...options.vitestOptions?.config,
        },
      });
    } else {
      this.deps.removeDependency("ts-jest");
    }

    /**
     * Specify package manager
     */
    this.package.file.addOverride(
      "packageManager",
      `pnpm@${options.pnpmVersion}`,
    );

    /**
     * Mark the package private unless it is actually released to a
     * registry. Removing `releaseToNpm` deletes the release workflow but
     * leaves a package anyone with scope auth can `npm publish` by hand;
     * `"private": true` makes npm itself refuse. Pass
     * `packagePrivate: false` to opt out.
     */
    applyPackagePrivate(this, options);

    /**
     *
     * Disable this rule for all test files.
     *
     * Unless we turn this off, ESLint will suggest that anything found in a test
     * needs to be a dep instead of a devDep. Projen actually already turns off
     * the rule for files in the tests/* folder but since we commonly put tests
     * next to the code it's testing, we need to do this ourselves.
     *
     */
    this.eslint?.addOverride({
      files: ["**/*.test.*", "**/*.spec.*"],
      rules: {
        "import/no-extraneous-dependencies": "off",
      },
    });

    /**
     * TSDoc / public-export documentation enforcement. Wires
     * `eslint-plugin-tsdoc` (for the `tsdoc/syntax` rule) and
     * `eslint-plugin-jsdoc` into the shared ESLint config.
     *
     * `tsdoc/syntax` ships as a warning. `jsdoc/require-jsdoc` defaults
     * to off — see the `requireJsdoc` option on
     * {@link TypeScriptProjectOptions} to opt back in.
     */
    this.addDevDeps("eslint-plugin-tsdoc", "eslint-plugin-jsdoc");
    this.eslint?.addPlugins("tsdoc", "jsdoc");
    this.eslint?.addRules({
      "tsdoc/syntax": "warn",
    });

    /**
     * Apply the requireJsdoc knob. The default is `"off"`, matching the
     * lint-noise mitigation in issue #645 — `jsdoc/require-jsdoc` was
     * firing on every un-documented function in consuming code (~1,000
     * warnings per build). Consumers that want the previous behaviour
     * pass `requireJsdoc: "public"` to scope it to exports only.
     */
    const requireJsdoc = options.requireJsdoc ?? "off";
    if (requireJsdoc !== "off") {
      const publicOnly = requireJsdoc === "public";
      this.eslint?.addOverride({
        files: ["src/**/*.ts"],
        excludedFiles: ["**/*.test.*", "**/*.spec.*", "**/*.generated.ts"],
        rules: {
          "jsdoc/require-jsdoc": [
            "warn",
            {
              publicOnly,
              require: {
                FunctionDeclaration: true,
                ClassDeclaration: true,
              },
              ...(publicOnly
                ? {
                    contexts: [
                      "ExportNamedDeclaration > FunctionDeclaration",
                      "ExportNamedDeclaration > ClassDeclaration",
                      "ExportNamedDeclaration > VariableDeclaration",
                      "ExportNamedDeclaration > TSInterfaceDeclaration",
                      "ExportNamedDeclaration > TSTypeAliasDeclaration",
                      "ExportNamedDeclaration > TSEnumDeclaration",
                    ],
                  }
                : {}),
              checkConstructors: false,
              checkGetters: false,
              checkSetters: false,
              enableFixer: false,
            },
          ],
        },
      });
    }

    /**
     * Emit `tsdoc.json` so `eslint-plugin-tsdoc` recognises workspace
     * scopes (`@codedrifters`, `@openhi`, etc.) and common JSDoc
     * convention tags (`@default`, `@experimental`, etc.). Without this,
     * downstream consumers see ~1,250 `tsdoc-at-sign-in-word` warnings
     * and ~17 `tsdoc-undefined-tag` warnings per build.
     *
     * Pass `tsdocConfig: false` to opt out (the file is not emitted and
     * the lint warnings return at full volume).
     */
    if (options.tsdocConfig !== false) {
      new TsdocConfig(
        this,
        typeof options.tsdocConfig === "object" ? options.tsdocConfig : {},
      );
    }

    /**
     * Wire `@microsoft/api-extractor` so the docs-sync pipeline can
     * produce a public-API rollup per package for drift detection.
     * Rollups are regenerated on scan (not committed to git) per the
     * docs-sync epic resolved decision #3. Pass `apiExtractor: false`
     * to opt out.
     */
    if (options.apiExtractor !== false) {
      new ApiExtractor(
        this,
        typeof options.apiExtractor === "object" ? options.apiExtractor : {},
      );
    }

    /**
     * Some standard ignores for all projects.
     */
    this.gitignore?.addPatterns(".DS_Store", "test-reports");
    this.npmignore?.addPatterns("*.spec.*", "*.test.*", "__fixtures__");

    /**
     * If turbo is active in the parent project, configure it here.
     */
    const turboActive =
      userOptions.parent && TurboRepo.of(userOptions.parent) !== undefined;
    if (turboActive) {
      const turbo = new TurboRepo(this);
      turbo.compileTask?.outputs.push("dist/**");
      turbo.compileTask?.outputs.push("lib/**");
      //turbo.testTask?.inputs.push("src/**");
    }

    /**
     * AI agent configuration — opt-in per project. Does not propagate from
     * parent MonorepoProject, so sub-projects don't render duplicate
     * `.cursor/` / `.claude/` / `CLAUDE.md` files into their own outdir.
     */
    if (
      options.agentConfig === true ||
      typeof options.agentConfig === "object"
    ) {
      new AgentConfig(
        this,
        typeof options.agentConfig === "object" ? options.agentConfig : {},
      );
    }

    /**
     * Add reset task if enabled.
     */
    if (options.resetTask !== false) {
      const defaultResetTaskOptions: ResetTaskOptions = {
        pathsToRemove: [
          "node_modules",
          "dist",
          "lib",
          "coverage",
          "test-reports",
          ".turbo",
          "tsconfig.tsbuildinfo",
          this.artifactsDirectory,
        ],
      };
      const userResetTaskOptions = options.resetTaskOptions ?? {};
      const resetTaskOptions: ResetTaskOptions = merge(
        defaultResetTaskOptions,
        {
          ...userResetTaskOptions,
          pathsToRemove: [
            ...(defaultResetTaskOptions.pathsToRemove ?? []),
            ...(userResetTaskOptions.pathsToRemove ?? []),
          ],
        },
      );
      new ResetTask(this, resetTaskOptions);
    }

    /**
     *
     */

    if (options.parent && options.parent instanceof MonorepoProject) {
      // Install dependencies via the parent project
      /* @ts-ignore access private method */
      const originalResolve = this.package.resolveDepsAndWritePackageJson;
      /* @ts-ignore access private method */
      this.package.installDependencies = () => {
        (options.parent as MonorepoProject).requestInstallDependencies({
          resolveDepsAndWritePackageJson: () =>
            originalResolve.apply(this.package),
        });
      };
      /* @ts-ignore access private method */
      this.package.resolveDepsAndWritePackageJson = () => {};
    }
  }
}
